import { type Component, ComponentType } from "@/types";
import { BaseCommandHandler, type CommandResult } from "../BaseCommandHandler";

const COLOR_MAP: Record<string, string> = {
  red: '#ef4444', blue: '#3b82f6', green: '#10b981', yellow: '#f59e0b',
  purple: '#8b5cf6', pink: '#ec4899', orange: '#f97316', cyan: '#06b6d4',
  indigo: '#6366f1', teal: '#14b8a6', gray: '#6b7280', grey: '#6b7280',
};

/**
 * Command Handler for Calendar components.
 *
 * Handles commands for:
 * - View switching (month/week/day/agenda)
 * - Weekend visibility
 * - First day of week
 * - Business hours
 * - Color mapping by category
 * - Week numbers
 * - Time slot interval
 * - Drag to reschedule toggle
 * - Inline event creation toggle
 * - External calendar connection
 */
export class CalendarCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // View switching: "switch to week view", "show month view", "show agenda"
    this.commandPatterns.set(
      /(?:switch to|show|change to|use) (?:the )?(month|week|day|agenda)(?: view)?/i,
      (match) => {
        return { properties: { viewMode: match[1].toLowerCase() } };
      }
    );

    // Hide/show weekends: "hide weekends", "show weekends"
    this.commandPatterns.set(
      /(hide|show|remove|display) (?:the )?weekends?/i,
      (match) => {
        const show = match[1].toLowerCase() === 'show' || match[1].toLowerCase() === 'display';
        return { properties: { showWeekends: show } };
      }
    );

    // First day of week: "start weeks on Sunday", "start weeks on Monday"
    this.commandPatterns.set(
      /(?:start|begin) (?:the )?weeks? on (sunday|monday|saturday)/i,
      (match) => {
        const dayMap: Record<string, number> = { sunday: 0, monday: 1, saturday: 6 };
        return { properties: { firstDayOfWeek: dayMap[match[1].toLowerCase()] ?? 1 } };
      }
    );

    // Business hours: "set business hours 9am to 5pm", "business hours from 8 to 18"
    this.commandPatterns.set(
      /(?:set )?business hours? (?:from )?(\d{1,2})(?::?\d{2})?(?:\s*(?:am|AM))?\s*(?:to|-)\s*(\d{1,2})(?::?\d{2})?(?:\s*(?:pm|PM))?/i,
      (match) => {
        let start = parseInt(match[1], 10);
        let end = parseInt(match[2], 10);
        // If end <= 12 and start < end, assume PM for end
        if (end <= 12 && start < end && match[0].toLowerCase().includes('pm')) {
          end += 0; // already parsed
        }
        // Normalize: if someone says "9am to 5pm", 5 → 17
        if (end <= start) end += 12;
        if (end > 24) end = 24;
        return { properties: { businessHoursStart: start, businessHoursEnd: end } };
      }
    );

    // Color mapping: "color meetings blue and deadlines red"
    this.commandPatterns.set(
      /colou?r (?:the )?(\w+)\s+(red|blue|green|yellow|purple|pink|orange|cyan|indigo|teal|gray|grey)(?:\s+and\s+(\w+)\s+(red|blue|green|yellow|purple|pink|orange|cyan|indigo|teal|gray|grey))?/i,
      (match, component) => {
        const existing = (component.properties?.colorMap || {}) as Record<string, string>;
        const updates: Record<string, string> = { ...existing };
        if (match[1] && match[2]) {
          updates[match[1].toLowerCase()] = COLOR_MAP[match[2].toLowerCase()] || match[2];
        }
        if (match[3] && match[4]) {
          updates[match[3].toLowerCase()] = COLOR_MAP[match[4].toLowerCase()] || match[4];
        }
        return { properties: { colorMap: updates } };
      }
    );

    // Week numbers: "show week numbers", "hide week numbers"
    this.commandPatterns.set(
      /(show|hide|display|remove) (?:the )?week numbers?/i,
      (match) => {
        const show = match[1].toLowerCase() === 'show' || match[1].toLowerCase() === 'display';
        return { properties: { showWeekNumbers: show } };
      }
    );

    // Time slot interval: "show 30-minute time slots", "use 30 minute slots"
    this.commandPatterns.set(
      /(?:show|use|set) (\d+)[\s-]*min(?:ute)? (?:time )?slots?/i,
      (match) => {
        const interval = parseInt(match[1], 10);
        if (interval === 30 || interval === 60) {
          return { properties: { timeSlotInterval: interval } };
        }
        return null;
      }
    );

    // Drag to reschedule: "enable drag to reschedule", "disable drag"
    this.commandPatterns.set(
      /(enable|disable|allow|prevent|turn on|turn off) (?:the )?drag(?:ging)?(?:\s+to\s+reschedule)?/i,
      (match) => {
        const enable = /enable|allow|turn on/i.test(match[1]);
        return { properties: { dragEnabled: enable } };
      }
    );

    // Inline event creation: "allow inline event creation", "enable event creation"
    this.commandPatterns.set(
      /(enable|disable|allow|prevent|turn on|turn off) (?:the )?(?:inline )?event creation/i,
      (match) => {
        const enable = /enable|allow|turn on/i.test(match[1]);
        return { properties: { inlineCreateEnabled: enable } };
      }
    );

    // Show/hide event time: "show event time", "hide event time"
    this.commandPatterns.set(
      /(show|hide) (?:the )?event times?/i,
      (match) => {
        return { properties: { showEventTime: match[1].toLowerCase() === 'show' } };
      }
    );

    // Max events per day: "show 5 events per day", "set max events to 4"
    this.commandPatterns.set(
      /(?:show|set|max) (\d+) events? (?:per day|per cell)/i,
      (match) => {
        return { properties: { maxEventsPerDay: parseInt(match[1], 10) } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.CALENDAR) return false;
    const keywords = [
      "view", "month", "week", "day", "agenda",
      "weekend", "week number", "business hour",
      "color", "colour", "time slot", "drag", "reschedule",
      "event creation", "inline", "event time",
      "connect", "google", "outlook", "calendar",
      "events per day", "max events",
    ];
    return keywords.some(kw => command.toLowerCase().includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      const validation = this.validateChanges(changes, component);
      return {
        success: true,
        command: {
          type: 'property',
          changes,
          confidence: 0.9,
          description: `Updated calendar: ${Object.keys(changes.properties || {}).join(', ')}`,
        },
        suggestions: [],
      };
    }

    // Check for external calendar connection commands
    if (/connect\s+(google|outlook|microsoft)/i.test(command)) {
      const provider = /google/i.test(command) ? 'google' : 'microsoft';
      return {
        success: true,
        command: {
          type: 'action',
          changes: {},
          confidence: 0.85,
          description: `Connect ${provider === 'google' ? 'Google Calendar' : 'Microsoft Outlook'} — open OAuth flow`,
          action: provider === 'google' ? 'connectGoogleCalendar' : 'connectMicrosoftCalendar',
        },
        suggestions: [],
      };
    }

    return {
      success: false,
      command: {
        type: 'property',
        changes: {},
        confidence: 0,
        description: 'Unrecognized calendar command',
      },
      suggestions: [
        'switch to week view',
        'hide weekends',
        'show week numbers',
        'set business hours 9am to 5pm',
        'color meetings blue',
        'enable drag to reschedule',
      ],
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'switch to week view',
      'hide weekends',
      'show week numbers',
      'set business hours 9am to 5pm',
      'color meetings blue',
      'enable drag to reschedule',
      'show 30-minute time slots',
    ];
  }

  async getSuggestions(_component: Component): Promise<string[]> {
    return [
      'switch to week view',
      'hide weekends',
      'show week numbers',
      'set business hours 9am to 5pm',
      'color meetings blue and deadlines red',
      'enable drag to reschedule',
    ];
  }
}

export const calendarCommandHandler = new CalendarCommandHandler();
